import React, { useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { History as HistoryIcon, Play } from 'lucide-react';

import GameMatchup from '@/shared/components/GameMatchup';
import ClipPlayerDialog from '@/features/clips/components/ClipLibrary/ClipPlayerDialog';
import { teamColorOf } from '@/features/clips/services/toClipLibraryItems';

import { useClipRuleActivity, type ClipRuleActivityEntry } from '../hooks/useClipRuleActivity';

/**
 * "Activity log" tab of the Automation page (C360-3154): every automated clip
 * group, newest first. Reads the clip groups themselves rather than a separate
 * audit trail — the group is the record of a rule firing.
 */

/** Export progress of the clips a single rule firing produced. */
const ClipProgress: React.FC<{ clips: ClipRuleActivityEntry['clips'] }> = ({ clips }) => {
  if (clips.total === 0) {
    return (
      <Tooltip title="The group was created but no live cameras were configured for the game">
        <Chip size="small" variant="outlined" label="no clips" />
      </Tooltip>
    );
  }
  return (
    <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
      <Chip
        size="small"
        variant="outlined"
        color={clips.ready === clips.total ? 'success' : 'default'}
        label={`${clips.ready}/${clips.total} ready`}
      />
      {clips.pending > 0 && <Chip size="small" variant="outlined" color="info" label={`${clips.pending} exporting`} />}
      {clips.failed > 0 && <Chip size="small" variant="outlined" color="error" label={`${clips.failed} failed`} />}
      {clips.canceled > 0 && <Chip size="small" variant="outlined" label={`${clips.canceled} canceled`} />}
    </Stack>
  );
};

type PlayableClip = React.ComponentProps<typeof ClipPlayerDialog>['clip'];

/** The library's player dialog, fed from an activity row instead of a library card. */
const toPlayableClip = (entry: ClipRuleActivityEntry, clip: ClipRuleActivityEntry['clips']['items'][number]) => {
  const { game } = entry;
  const away = game?.awayTeam.abbreviation;
  const home = game?.homeTeam.abbreviation;
  return {
    id: String(clip.id),
    status: clip.status ?? '',
    title: entry.name,
    gameLabel: away && home ? `${away} vs ${home}` : (game?.name ?? ''),
    meta: [entry.period, entry.gameTime].filter(Boolean).join(' · '),
    teamAbbreviation: home ?? '',
    teamLogoUrl: game?.homeTeam.logoUrl ?? null,
    teamColor: teamColorOf(game?.homeTeam),
    thumbnailUrl: null,
  };
};

const ClipRuleActivity: React.FC = () => {
  const { data, isLoading, error } = useClipRuleActivity();
  const activity = data?.activity ?? [];
  // Lets ops play back what a rule produced without leaving the log.
  const [activeClip, setActiveClip] = useState<PlayableClip>(null);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="error" sx={{ py: 4 }}>
        Could not load the activity log: {(error as Error).message}
      </Typography>
    );
  }

  if (activity.length === 0) {
    return (
      <Stack sx={{ alignItems: 'center', justifyContent: 'center', gap: 2, py: 10, textAlign: 'center' }}>
        <Box sx={{ color: 'text.disabled' }}>
          <HistoryIcon size={36} />
        </Box>
        <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Nothing clipped yet</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, lineHeight: 1.6 }}>
          Once an enabled rule matches an event in a live game, every clip it creates shows up here.
        </Typography>
      </Stack>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography sx={{ fontSize: 18, fontWeight: 700, mb: 0.5 }}>Recent automated clips</Typography>
      <Typography variant="caption" color="text.secondary">
        The {activity.length} most recent clips created by a rule, newest first.
      </Typography>

      <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>Rule</TableCell>
              <TableCell>Game</TableCell>
              <TableCell>Clip</TableCell>
              <TableCell>Game clock</TableCell>
              <TableCell>Export</TableCell>
              <TableCell>View</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activity.map((entry) => (
              <TableRow key={entry.clipGroupId} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title={new Date(entry.createdAt).toLocaleString()}>
                    <span>{new Date(entry.createdAt).toLocaleTimeString()}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {/* Rules created before this log existed have no attribution,
                      and deleting a rule sets it back to null. */}
                  {entry.rule?.name ?? <em>unattributed</em>}
                </TableCell>
                <TableCell sx={{ minWidth: 300 }}>
                  {entry.game?.uniqueId ? (
                    <Tooltip title={entry.game.name}>
                      <Link to={`/games/${entry.game.uniqueId}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                        <GameMatchup game={entry.game} />
                      </Link>
                    </Tooltip>
                  ) : entry.game ? (
                    <GameMatchup game={entry.game} />
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell>{entry.name}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {[entry.period, entry.gameTime].filter(Boolean).join(' · ') || '—'}
                </TableCell>
                <TableCell>
                  <ClipProgress clips={entry.clips} />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {/* One button per clip (one per camera). Not-yet-ready clips
                      open too: the dialog says why there is nothing to play. */}
                  {entry.clips.items.length === 0
                    ? '—'
                    : entry.clips.items.map((clip, index) => (
                        <Tooltip key={clip.id} title={`Clip ${index + 1} · ${(clip.status ?? '').toLowerCase()}`}>
                          <IconButton
                            size="small"
                            aria-label={`View clip ${index + 1}`}
                            onClick={() => setActiveClip(toPlayableClip(entry, clip))}
                          >
                            <Play size={16} />
                          </IconButton>
                        </Tooltip>
                      ))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ClipPlayerDialog clip={activeClip} onClose={() => setActiveClip(null)} />
    </Box>
  );
};

export default ClipRuleActivity;
